import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import {
  ArrowLeft,
  Check,
  Clock,
  XCircle,
  Receipt,
  CreditCard,
  BadgeCheck,
  Lock,
  Sparkles,
} from 'lucide-react';
import { getPaymentHistory, PaymentHistoryItem } from '../services/api';
import ekoIcon from '../assets/eko-_transparent-removebg-preview.png';

const formatUsd = (cents: number) =>
  `$${(cents / 100) % 1 === 0 ? cents / 100 : (cents / 100).toFixed(2)}`;

const formatDate = (iso: string) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const STATUS_META: Record<
  PaymentHistoryItem['status'],
  { label: string; icon: React.FC<any>; bg: string; text: string; border: string }
> = {
  completed: {
    label: 'Completed',
    icon: Check,
    bg: 'bg-green-50',
    text: 'text-green-700',
    border: 'border-green-200',
  },
  pending: {
    label: 'Pending',
    icon: Clock,
    bg: 'bg-yellow-50',
    text: 'text-yellow-700',
    border: 'border-yellow-200',
  },
  expired: {
    label: 'Expired',
    icon: XCircle,
    bg: 'bg-red-50',
    text: 'text-red-600',
    border: 'border-red-200',
  },
  failed: {
    label: 'Failed',
    icon: XCircle,
    bg: 'bg-red-50',
    text: 'text-red-600',
    border: 'border-red-200',
  },
};

export const PaymentHistory: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<PaymentHistoryItem[]>([]);
  const [counts, setCounts] = useState({ total: 0, completed: 0, pending: 0, expired: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      setError('');
      try {
        const data = await getPaymentHistory();
        if (cancelled) return;
        setItems(data.items || []);
        setCounts({
          total: data.total || 0,
          completed: data.completed || 0,
          pending: data.pending || 0,
          expired: data.expired || 0,
        });
      } catch (err: any) {
        if (!cancelled) setError(err?.message || 'Failed to load payment history.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (isLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: '#F3EEE8' }}
      >
        <div className="text-center">
          <img
            src={ekoIcon}
            alt="Eko mascot"
            className="w-20 h-20 mx-auto mb-4 animate-gentle-bounce object-contain"
          />
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-[#F7B26A] border-t-transparent"></div>
          <p className="mt-4 text-lg text-[#1F224B] font-semibold">Loading history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-6 px-4 sm:py-8" style={{ backgroundColor: '#F3EEE8' }}>
      {/* Decorative background blobs */}
      <div className="fixed -top-10 -left-10 w-72 h-48 bg-gradient-to-r from-[#FFF2E6] to-[#FFF0F6] rounded-full opacity-50 blur-3xl pointer-events-none -z-10" />
      <div className="fixed top-1/2 -right-16 w-72 h-48 bg-gradient-to-r from-[#EDE9FF] to-[#FFF0F6] rounded-full opacity-50 blur-3xl pointer-events-none -z-10" />

      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <header className="w-full bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] text-white py-5 px-6 rounded-2xl shadow-md mb-6">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/app')}
              className="flex items-center gap-2 bg-white/20 hover:bg-white/30 rounded-xl px-3 py-2 transition-colors text-sm font-semibold"
              aria-label="Back to dashboard"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back</span>
            </button>
            <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
              <Receipt className="w-5 h-5" />
              Payment History
            </h1>
            <div className="w-16" />
          </div>
        </header>

        {error && (
          <Card className="p-4 mb-6 bg-red-50 border-2 border-red-200 rounded-xl">
            <p className="text-red-600 font-semibold">{error}</p>
          </Card>
        )}

        {/* Summary stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <Card className="p-4 bg-white/95 backdrop-blur border-2 border-[#F0EEFF] rounded-2xl shadow-sm">
            <div className="text-2xl font-bold text-[#1F224B]">{counts.total}</div>
            <div className="text-xs text-[#6B6FA3] font-medium">Total</div>
          </Card>
          <Card className="p-4 bg-white/95 backdrop-blur border-2 border-green-100 rounded-2xl shadow-sm">
            <div className="text-2xl font-bold text-green-700 flex items-center gap-1">
              <Check className="w-5 h-5" />
              {counts.completed}
            </div>
            <div className="text-xs text-[#6B6FA3] font-medium">Completed</div>
          </Card>
          <Card className="p-4 bg-white/95 backdrop-blur border-2 border-yellow-100 rounded-2xl shadow-sm">
            <div className="text-2xl font-bold text-yellow-700 flex items-center gap-1">
              <Clock className="w-5 h-5" />
              {counts.pending}
            </div>
            <div className="text-xs text-[#6B6FA3] font-medium">Pending</div>
          </Card>
          <Card className="p-4 bg-white/95 backdrop-blur border-2 border-red-100 rounded-2xl shadow-sm">
            <div className="text-2xl font-bold text-red-600 flex items-center gap-1">
              <XCircle className="w-5 h-5" />
              {counts.expired}
            </div>
            <div className="text-xs text-[#6B6FA3] font-medium">Expired</div>
          </Card>
        </div>

        {/* History list */}
        {items.length === 0 ? (
          <Card className="p-8 rounded-3xl bg-white/95 backdrop-blur border-4 shadow-xl text-center max-w-md mx-auto">
            <img
              src={ekoIcon}
              alt="Eko mascot"
              className="w-24 h-24 mx-auto mb-4 animate-gentle-float object-contain"
            />
            <div className="w-16 h-16 rounded-full bg-[#F5F3FF] flex items-center justify-center mx-auto mb-4">
              <Receipt className="w-8 h-8 text-[#8E7BFF]" />
            </div>
            <h3 className="text-2xl font-bold text-[#1F224B] mb-3">No payments yet</h3>
            <p className="text-sm text-[#6B6FA3] mb-6 max-w-sm mx-auto">
              When you unlock a child's assessments, your payment records and
              statuses will appear here.
            </p>
            <button
              onClick={() => navigate('/app/payment')}
              className="w-full h-14 rounded-2xl font-bold text-lg text-white bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] hover:brightness-105 transition-all shadow-lg btn-bouncy"
            >
              Unlock Assessments
            </button>
          </Card>
        ) : (
          <div className="space-y-4">
            {items.map((item) => {
              const meta = STATUS_META[item.status] || STATUS_META.pending;
              const StatusIcon = meta.icon;
              return (
                <Card
                  key={item.payment_id}
                  className="p-5 sm:p-6 rounded-3xl bg-white/95 backdrop-blur border-4 border-[#F0EEFF] shadow-md hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    {/* Left: amount + date */}
                    <div className="flex items-start gap-3 min-w-0">
                      <div className="w-11 h-11 flex-none rounded-full bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] flex items-center justify-center shadow-sm">
                        <CreditCard className="w-5 h-5 text-white" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-lg font-bold text-[#1F224B]">
                          {formatUsd(item.amount_cents)}
                          <span className="text-sm font-medium text-[#6B6FA3] ml-1 uppercase">
                            {item.currency}
                          </span>
                        </div>
                        <div className="text-xs text-[#6B6FA3] mt-0.5">
                          {formatDate(item.created_at)}
                        </div>
                        {item.completed_at && (
                          <div className="text-xs text-green-600 mt-0.5">
                            Completed {formatDate(item.completed_at)}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Right: status badge */}
                    <span
                      className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border ${meta.bg} ${meta.text} ${meta.border}`}
                    >
                      <StatusIcon className="w-3.5 h-3.5" />
                      {meta.label}
                    </span>
                  </div>

                  {/* Children */}
                  {item.children.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-[#EDE9FF]">
                      <div className="text-xs font-semibold text-[#6B6FA3] mb-2 uppercase tracking-wide">
                        {item.children.length === 1 ? 'Profile' : 'Profiles'}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {item.children.map((child) => (
                          <span
                            key={child.child_id}
                            className={`inline-flex items-center gap-1.5 text-sm font-medium rounded-full px-3 py-1.5 border ${
                              child.payment_status === 'paid'
                                ? 'bg-green-50 text-green-700 border-green-200'
                                : 'bg-[#F5F3FF] text-[#8E7BFF] border-[#8E7BFF]/30'
                            }`}
                          >
                            {child.payment_status === 'paid' ? (
                              <BadgeCheck className="w-3.5 h-3.5" />
                            ) : (
                              <Lock className="w-3.5 h-3.5" />
                            )}
                            {child.name || 'Unknown'}
                            {child.grade && (
                              <span className="opacity-70 text-xs">· {child.grade}</span>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Transaction id */}
                  <div className="mt-3 text-xs text-[#6B6FA3] font-mono break-all">
                    ID: {item.payment_id}
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Footer CTA */}
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/app/payment')}
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#8E7BFF] hover:text-[#1F224B] transition-colors"
          >
            <Sparkles className="w-4 h-4" />
            Unlock more profiles
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentHistory;
